import React from 'react';
import { FormTextField } from '../../components/Form/TextField/FormTextField';
import { t } from '../../locales/translate';
import { FormSearch } from '../../components/Form/AsyncSearch/FormSearch';
import { Stack } from '../../components/Stack/Stack';
import { FormNumberField } from '../../components/Form/NumberField/FormNumberField';
import { FormSelect } from '../../components/Form/Select/FormSelect';
import { Text } from '../../components/text/text';
import { Spacer } from '../../components/Spacer/Spacer';
import { VerticalRadioGroup } from '../../components/Form/Radio/VerticalRadioGroup';
import { FormPercentageField } from '../../components/Form/TextField/FormPercentageField';
import { formatPercentage } from '../../util/formatters';
import { VoucherCampaign } from '../../generated/graphql';
import { MappedFields } from '@nerdgeschoss/react-use-form-library/dist/field';

interface Props {
  fields: MappedFields<Partial<VoucherCampaign>>;
}

export function VoucherCampaignFormFields({ fields }: Props): JSX.Element {
  return (
    <>
      <FormSearch {...fields.shopId} name="shopName" searchIn={['shop']} label={t.page.voucherCampaigns.form.shop} />
      <FormTextField {...fields.title} name="title" label={t.page.voucherCampaigns.form.title} />
      <FormTextField
        {...fields.internalTitle}
        name="internalTitle"
        label={t.page.voucherCampaigns.form.internalTitle}
      />
      <VerticalRadioGroup
        {...fields.target}
        label={t.page.voucherCampaigns.form.target}
        description={t.page.voucherCampaigns.form.targetDescription}
        name="target"
        layout="list"
        stackSize="xs"
        options={[
          { value: 'PRODUCT', label: t.page.voucherCampaigns.form.targetProduct },
          { value: 'SHIPPING', label: t.page.voucherCampaigns.form.targetShipping, disabled: true },
        ]}
      />
      <Stack size="xxxs">
        <Stack line size="16" align="start">
          {fields.discountType.value === 'PERCENTAGE' ? (
            <FormPercentageField
              {...fields.amount}
              name="amount"
              icon="percent"
              label={t.page.voucherCampaigns.form.amount}
            />
          ) : (
            <FormNumberField
              {...fields.amount}
              name="amount"
              icon="money"
              label={t.page.voucherCampaigns.form.amount}
            />
          )}
          <FormSelect
            {...fields.discountType}
            name="discountType"
            options={[
              { value: 'PERCENTAGE', label: t.page.voucherCampaigns.form.discountTypePercentage },
              { value: 'ABSOLUTE', label: t.page.voucherCampaigns.form.discountTypeFixed },
            ]}
            label={t.page.voucherCampaigns.form.type}
            onChange={(value) => {
              const discountType = value as 'PERCENTAGE' | 'ABSOLUTE';

              if (fields.amount.value) {
                if (discountType === 'PERCENTAGE') {
                  fields.amount.onChange(fields.amount.value / 100);
                }
                if (discountType === 'ABSOLUTE') {
                  fields.amount.onChange(fields.amount.value * 100);
                }
                fields.amount.touch();
              }

              fields.discountType.onChange(discountType);
            }}
          />
        </Stack>
        <Stack line size="zero">
          <Spacer width={34} />
          <Text type="form-description">{t.page.voucherCampaigns.form.amountDescription}</Text>
        </Stack>
      </Stack>
      <FormPercentageField
        {...fields.costSplit}
        icon="percent"
        label={t.page.voucherCampaigns.form.costSplit}
        description={
          <span
            dangerouslySetInnerHTML={{
              __html: t.page.voucherCampaigns.form.costSplitDescription(formatPercentage(fields.costSplit.value)),
            }}
          />
        }
      />
      <VerticalRadioGroup
        {...fields.usage}
        label={t.page.voucherCampaigns.form.voucherUsage}
        description={t.page.voucherCampaigns.form.voucherUsageDescription}
        name="usage"
        layout="list"
        stackSize="xs"
        options={[
          { value: 'MULTIPLE', label: t.page.voucherCampaigns.form.multiple },
          { value: 'SINGLE', label: t.page.voucherCampaigns.form.single },
        ]}
      />
    </>
  );
}
